import {
  useContext,
  useEffect,
  useRef,
  useState,
  FunctionComponent,
} from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  LinearProgress,
  Paper,
  TableContainer,
  TablePagination,
  Skeleton,
} from "@mui/material";
import "../../styles/global.css";
import AppStateContext from "../../contexts/AppStateContext";
import DashboardTableHeadCell, {
  DashboardTableRefreshCell,
} from "components/Dashboard/DashboardTableHeadCell";
import { SKELETON_ROW_HEIGHT } from "components/Dashboard/dashboardTable";
import useStateWithSessionStorage from "hooks/UseStateWithSessionStorage";
import OutsourcingRow, { INQUIRY_COLUMN_COUNT } from "./OutsourcingRow";
import { InquiryType } from "types/InquiryTypes";
import { apiGetInquiries, InquiryListScope } from "util/network/Inquiries";
import { StatusAck } from "hooks/useInquiryStatusUpdates";

type PropsType = {
  searchValue: string;
  statusFilter: string[];
  // Omit and the rows are not clickable - see OutsourcingRow.
  openInquiry?: (inquiry: InquiryType) => void;
  refresh: boolean;
  setRefresh: (refresh: boolean) => void;
  orderId?: number;
  // EE-239: space-separated `attr<op>value` tokens for the backend's generic
  // filter param, built by InquiryFilterBar's owner.
  filter?: string;
  // The board's Mine / All tabs. Omit for every inquiry.
  scope?: InquiryListScope;
  // A row's status marker changed (InquiryDialog's onStatusAck). Patched into
  // the rows in place: a refresh would swap the table for skeletons to redraw
  // one marker.
  statusAck?: StatusAck;
};

// Inquiry table on outsourcing management page
const OutsourcingTable: FunctionComponent<PropsType> = ({
  searchValue,
  statusFilter,
  openInquiry,
  refresh,
  setRefresh,
  orderId,
  filter,
  scope,
  statusAck,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { token } = useContext(AppStateContext);
  const [total, setTotal] = useState<number>(0);
  // Persisted so leaving and returning restores the list, matching the
  // Requisition dashboard (EE-238).
  const [page, setPage] = useStateWithSessionStorage<number>(
    0,
    "inquiryPage",
  );
  const [pageSize, setPageSize] = useStateWithSessionStorage<number>(
    25,
    "inquiryPageSize",
  );
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [sortKey, setSortKey] = useState<string>("created");
  const [inquiries, setInquiries] = useState<InquiryType[]>([]);

  //changes the page number
  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };
  //changes the number of rows per page
  const handleChangeRowsPerPage = (event: any) => {
    setPageSize(+event.target.value);
    setPage(0);
  };

  // A narrowed result set must not land on a page that no longer exists, so
  // reset paging when the filter or the scope changes. Adjusting during render
  // (rather than in an effect) means the fetch below already sees page 0 and
  // we don't fire a throwaway request for the stale page first.
  const listKey = `${scope ?? ""}|${filter ?? ""}`;
  const [appliedListKey, setAppliedListKey] = useState(listKey);
  if (listKey !== appliedListKey) {
    setAppliedListKey(listKey);
    setPage(0);
  }

  useEffect(() => {
    refreshInquiries().then((res) => {
      setRefresh(false);
    });
  }, [
    refresh,
    statusFilter,
    searchValue,
    filter,
    scope,
    page,
    pageSize,
    sortKey,
    sortAsc,
  ]);

  useEffect(() => {
    if (!statusAck) return;
    setInquiries((rows) =>
      rows.map((row) =>
        row.id === statusAck.id
          ? { ...row, statusAckPending: statusAck.pending }
          : row
      )
    );
  }, [statusAck]);

  // Filters change faster than requests come back. Only the newest request may
  // write to the table, otherwise a slow earlier response lands last and the
  // rows stop matching the filter bar above them.
  const latestRequestRef = useRef<number>(0);

  //refreshes the inquiries
  const refreshInquiries = () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    return (
      apiGetInquiries(
        token,
        page + 1,
        pageSize,
        sortKey,
        sortAsc ? "asc" : "desc",
        statusFilter.join("+"),
        searchValue,
        // The order detail page passes an orderId instead of using the filter
        // bar; both are the same space-separated token format, so they merge.
        [orderId ? `order_id=${orderId}` : "", filter ?? ""]
          .filter((part) => !!part)
          .join(" "),
        scope
      )
        .then((res) => {
          if (latestRequestRef.current !== requestId) return;
          setInquiries(res.items);
          setTotal(res.total);
          setLoading(false);
        })
        // Without this the spinner runs forever on any failure - the endpoint
        // answers text/plain on a 500, so apiGetInquiries' res.json() rejects.
        .catch((err) => {
          if (latestRequestRef.current !== requestId) return;
          console.error("Failed to load inquiries:", err);
          setLoading(false);
        })
    );
  };

  // Sort keys are the backend's fixed list (_sort_column in
  // InquiryController), each ordering by what its column shows: status in flow
  // order, supplier by company name, raised by by email, requirement by title.
  // A key outside that list is a 400.
  const doSort = (key: string) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
    // A re-sorted list is a different list. Staying on page 4 of it shows rows
    // the user did not ask to be taken to.
    setPage(0);
  };

  // Only mark the header the list is actually ordered by, and only let that
  // one advertise a direction - an inactive header offering "asc" is a lie,
  // since the first click on it sorts descending.
  const sortProps = (key: string) => ({
    onSort: () => doSort(key),
    sortActive: sortKey === key,
    sortDirection: (sortKey === key && sortAsc ? "asc" : "desc") as "asc" | "desc",
    disabled: loading,
  });

  return (
    <Paper sx={{ width: "100%" }}>
      {loading ? <LinearProgress /> : null}

      <TableContainer>
        <Table>
          <TableHead
            sx={{
              background: "var(--header)",
            }}
          >
            <TableRow>
              <DashboardTableHeadCell {...sortProps("status")}>
                Status
              </DashboardTableHeadCell>
              <DashboardTableHeadCell {...sortProps("id")}>
                Inquiry
              </DashboardTableHeadCell>
              <DashboardTableHeadCell {...sortProps("requirement")}>
                Requirement
              </DashboardTableHeadCell>
              <DashboardTableHeadCell>Items</DashboardTableHeadCell>
              <DashboardTableHeadCell {...sortProps("supplier")}>
                Supplier
              </DashboardTableHeadCell>
              <DashboardTableHeadCell {...sortProps("raisedBy")}>
                Raised by
              </DashboardTableHeadCell>
              <DashboardTableHeadCell align="right" {...sortProps("delivery_date")}>
                Deadline
              </DashboardTableHeadCell>
              <DashboardTableHeadCell align="right">
                Delivery status
              </DashboardTableHeadCell>
              {/* Last, because it is the widest column and the only free text
                  on the board: between the figures it pushed everything after
                  it out of alignment. */}
              <DashboardTableHeadCell>Latest note</DashboardTableHeadCell>
              <DashboardTableRefreshCell
                onRefresh={() => refreshInquiries()}
                disabled={loading}
              />
            </TableRow>
          </TableHead>
          {loading ? (
            <TableBody>
              {Array.from({ length: pageSize }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={INQUIRY_COLUMN_COUNT}>
                    <Skeleton variant="rectangular" height={SKELETON_ROW_HEIGHT} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : inquiries.length > 0 ? (
            <TableBody>
              {inquiries.map((inquiry: InquiryType) => {
                return (
                  <OutsourcingRow
                    key={inquiry.id}
                    inquiry={inquiry}
                    refresh={refreshInquiries}
                    open={openInquiry}
                  />
                );
              })}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={INQUIRY_COLUMN_COUNT} style={{ textAlign: "center" }}>
                  No inquiries found
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default OutsourcingTable;
